import { CommonModule, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ErrorMessageComponent } from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { Dropdown } from '@com-delta-omnichannel/mach-core-js';

import { PopUpModal } from '@com-delta-idp/mnx-sitewide-shared-utils';
import { DropdownOption } from '../../models/interfaces/common.interface';
import { SearchTrip } from '../../models/interfaces/my-trip.interfaces';

@Component({
  selector: 'idp-my-trip-search',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, NgIf, ReactiveFormsModule, ErrorMessageComponent],
  templateUrl: './trip-search.component.html',
  styleUrls: ['./trip-search.component.scss']
})
export class MyTripSearchComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input()
  content!: SearchTrip | null;

  @Input() isSelected = false;

  @Output() formSubmit: EventEmitter<string> = new EventEmitter<string>();
  @Output() findTripByChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() openDialogModal: EventEmitter<PopUpModal> =
    new EventEmitter<PopUpModal>();

  @Output() displayFindTrip: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  @Output() analyticsEvent = new EventEmitter<HTMLElement>();

  @ViewChild('tripWidgetSearchForm')
  tripWidgetForm!: ElementRef;

  focusedFields: { [key: string]: boolean } = {};
  private dropdown!: Dropdown;

  constructor(public elementRef: ElementRef) {}

  ngAfterViewInit(): void {
    if (!this.content) return;

    this.dropdown = new Dropdown({
      id: this.content.dropdownId,
      title: this.content.findYourTripBy,
      placeholder: this.content.dropdownPlaceholder,
      items: this.content.option
    });

    this.dropdown.onChange((item) => {
      if (this.content && this.findTripByChange.observers.length === 0) {
        this.content.currentType = item.value;
      }

      this.setFindTripByType({ value: item.value, label: item.label });
    });
    const defaultItem = this.content.dataList?.find(
      (item) => item.default === 'true'
    );
    if (defaultItem) {
      this.dropdown.setValue(defaultItem.value);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSelected']?.currentValue && this.content) {
      this.content.submitClicked = false;
    }
  }

  onSubmit = (): void => this.formSubmit.emit();

  setFindTripByType = (event: DropdownOption): void =>
    this.findTripByChange.emit(event.value);

  openDialog = (popup: PopUpModal): void => this.openDialogModal.emit(popup);

  focusFirstInvalidFieldHandler = (): void => {
    const firstInvalid = this.tripWidgetForm?.nativeElement.querySelector(
      '.ng-invalid:not(form)'
    );
    firstInvalid?.focus();
  };

  displayTripDetails = (): void => this.displayFindTrip.emit(false);

  shouldShowError(fieldName: string): boolean {
    const control = this.content?.formGroup.controls[fieldName];
    return (control?.invalid && this.content?.submitClicked) || false;
  }

  get hasFormErrors(): boolean {
    if (!this.content?.submitClicked || !this.content?.formGroup) return false;
    return Object.keys(this.content.formGroup.controls).some(
      (key) => this.content?.formGroup.controls[key].invalid
    );
  }

  get currentErrorCount(): number {
    if (!this.content?.formGroup) return 0;
    return Object.keys(this.content.formGroup.controls).filter(
      (key) => this.content?.formGroup.controls[key].invalid
    ).length;
  }

  ngOnDestroy(): void {
    this.dropdown?.destroy();
  }
}
